import { useEffect, useRef, useState, useCallback } from "react";
import { ArrowLeft, Send, Loader2, Mail, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const THREAD_POLL_MS = 6000;

function ChatThread({ conversation, onBack, onChanged }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/chat/conversations/${conversation.id}/messages`);
      setMessages(data.items || []);
    } catch {
      toast.error("Failed to load conversation");
    } finally {
      setLoading(false);
    }
  }, [conversation.id]);

  useEffect(() => {
    load();
    const interval = setInterval(load, THREAD_POLL_MS);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  async function send(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    setDraft("");
    try {
      const { data } = await api.post(`/admin/chat/conversations/${conversation.id}/messages`, { message: text });
      setMessages((m) => [...m, data]);
      onChanged();
    } catch {
      toast.error("Failed to send");
    } finally {
      setSending(false);
    }
  }

  async function closeConversation() {
    setClosing(true);
    try {
      await api.post(`/admin/chat/conversations/${conversation.id}/close`);
      toast.success("Conversation closed");
      onChanged();
    } catch {
      toast.error("Could not close conversation");
    } finally {
      setClosing(false);
    }
  }

  return (
    <div className="card-dark flex flex-col h-[600px]" data-testid="chat-admin-thread">
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div>
          <button onClick={onBack} className="text-white/50 hover:text-white text-xs inline-flex items-center gap-1 mb-1">
            <ArrowLeft size={12} /> All conversations
          </button>
          <h3 className="font-semibold text-white">{conversation.visitor_name}</h3>
          {conversation.visitor_email && (
            <a href={`mailto:${conversation.visitor_email}`} className="text-xs text-white/50 hover:text-[var(--gold)] flex items-center gap-1">
              <Mail size={11} /> {conversation.visitor_email}
            </a>
          )}
        </div>
        {conversation.status !== "closed" && (
          <button onClick={closeConversation} disabled={closing} className="text-xs text-white/50 hover:text-[var(--gold)]">
            {closing ? <Loader2 size={13} className="animate-spin" /> : "Close chat"}
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && <div className="text-center py-6"><Loader2 size={20} className="animate-spin text-[var(--gold)] mx-auto" /></div>}
        {!loading && messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "staff" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${
                m.sender === "staff" ? "bg-[var(--gold)] text-[#0a0a0a] rounded-br-sm" : "bg-white/10 text-white rounded-bl-sm"
              }`}
            >
              {m.sender === "staff" && <p className="text-[10px] uppercase tracking-wide opacity-60 mb-0.5">{m.sender_name}</p>}
              <p className="whitespace-pre-wrap break-words">{m.message}</p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a reply..."
          className="input-dark text-sm flex-1"
          data-testid="chat-admin-input"
        />
        <button type="submit" disabled={sending || !draft.trim()} className="btn-gold px-3.5 shrink-0" data-testid="chat-admin-send">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
    </div>
  );
}

export default function ChatPanel({ conversations, loadingChats, onRefresh = () => {} }) {
  const [selected, setSelected] = useState(null);

  if (selected) {
    const current = conversations.find((c) => c.id === selected) || { id: selected, visitor_name: "Visitor" };
    return <ChatThread conversation={current} onBack={() => setSelected(null)} onChanged={onRefresh} />;
  }

  return (
    <div className="space-y-3" data-testid="admin-chat-panel">
      {loadingChats && <div className="text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>}
      {!loadingChats && conversations.length === 0 && (
        <div className="card-dark p-12 text-center text-white/55" data-testid="admin-no-chats">
          No conversations yet. They'll appear here when a visitor uses the chat widget.
        </div>
      )}
      {conversations.map((c) => (
        <button
          key={c.id}
          onClick={() => setSelected(c.id)}
          className={`card-dark p-4 w-full text-left hover:border-[var(--gold)]/30 transition flex items-center justify-between gap-3 ${
            c.unread_by_staff ? "border-[var(--gold)]/40" : ""
          }`}
          data-testid={`chat-row-${c.id}`}
        >
          <div className="flex items-center gap-3">
            {c.unread_by_staff ? (
              <Circle size={9} fill="currentColor" className="text-[var(--gold)] shrink-0" />
            ) : (
              <CheckCircle2 size={13} className="text-white/25 shrink-0" />
            )}
            <div>
              <p className={`font-medium ${c.unread_by_staff ? "text-white" : "text-white/70"}`}>{c.visitor_name}</p>
              {c.visitor_email && <p className="text-xs text-white/45">{c.visitor_email}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/40">
            {c.status === "closed" && <span className="px-2 py-0.5 rounded-full border border-white/15">Closed</span>}
            {new Date(c.updated_at).toLocaleString()}
          </div>
        </button>
      ))}
    </div>
  );
}
